'use client';

import { useRouter, usePathname } from 'next/navigation';
import { useState, type MouseEvent, type ReactNode } from 'react';
import { Collapse } from '@mui/material';
import { UserRole } from '@/lib/types';
import { DocumentTypeCode } from '@/apiClient';
import { usePrivate } from '@/components/private/PrivateContext';
import { useLogout } from '@/hooks/useLogout';
import { useIsAbriStaff } from '@/hooks/useIsAbriStaff';
import { MenuIcon } from '@/primitives/icons';
import {
  Box,
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from '@/primitives';
import { DocumentAvatar } from '@/components/avatars/DocumentAvatar';
import { userAvatarMessages } from './messages';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import AppRegistrationOutlinedIcon from '@mui/icons-material/AppRegistrationOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import SyncIcon from '@mui/icons-material/Sync';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import type { SvgIconComponent } from '@mui/icons-material';
import { APP_ROUTE_PATHS, PUBLIC_ROUTES } from '@/configs/routes';

export interface UserAvatarProps {
  /** When true, uses smaller avatar, text, and menu icon (e.g. for society header). */
  compact?: boolean;
  /** When true, renders menu items as an inline collapsible instead of a popover. */
  inline?: boolean;
  /**
   * Trailing affordance on the trigger. `chevron` reads as "opens a dropdown",
   * which suits a marketing header; `inline` always uses the chevron.
   */
  trailingIcon?: 'menu' | 'chevron';
}

const menuItemSx = {
  py: '6px',
  px: '12px',
  minHeight: 0,
  gap: '8px',
  fontSize: '13px',
  '& .MuiListItemIcon-root': {
    minWidth: 0,
    color: 'text.secondary',
  },
} as const;

const inlineItemSx = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  px: '4px',
  py: '6px',
  fontSize: '13px',
  color: 'text.secondary',
  cursor: 'pointer',
  borderRadius: '4px',
  '&:hover': { backgroundColor: 'action.hover' },
} as const;

export function UserAvatar({
  compact = false,
  inline = false,
  trailingIcon = 'menu',
}: UserAvatarProps) {
  const router = useRouter();
  const pathname = usePathname();
  const isOnManageUsersPage = pathname.endsWith('/account/manage-users');
  const isOnSolrSyncPage = pathname.includes('/registry/solr-sync');
  const isOnSocietySetupPage = pathname.endsWith('/account/society-setup');
  const isOnRegistryPage =
    pathname === PUBLIC_ROUTES.REGISTRY_LANDING ||
    pathname.includes('/registry/');
  const isOnAccountPage =
    pathname.startsWith('/account') &&
    !isOnManageUsersPage &&
    !isOnSocietySetupPage;
  const isOnAdminPage =
    isOnManageUsersPage || isOnSolrSyncPage || isOnSocietySetupPage;
  const isAbriStaff = useIsAbriStaff();
  const {
    userProfile,
    userEmail,
    userOrganisations,
    selectedOrganisationId,
    selectedOrganisation,
  } = usePrivate();
  const { logout } = useLogout();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [expanded, setExpanded] = useState<boolean>(false);
  const [adminExpanded, setAdminExpanded] = useState<boolean>(isOnAdminPage);

  const open = Boolean(anchorEl);

  const userOrganisationsFound = userOrganisations?.find(
    (o) => o.organisationId === selectedOrganisationId,
  );

  const handleClickUserProfileButton = (event: MouseEvent<HTMLDivElement>) => {
    if (inline) {
      setExpanded((prev) => !prev);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleClickAccount = () => {
    setAnchorEl(null);
    setExpanded(false);
    router.push('/account');
  };

  const handleNavigate = (href?: string) => {
    if (!href) return;
    setAnchorEl(null);
    setExpanded(false);
    router.push(href);
  };

  // The context-level `selectedOrganisation` (full DTO) is only populated when
  // the user actively switches orgs via the sidebar. On initial load/refresh
  // only `selectedOrganisationId` is hydrated, so fall back to resolving the
  // org slug from the membership list to keep these links working.
  const orgSlug =
    selectedOrganisation?.name ??
    userOrganisations?.find((o) => o.organisationId === selectedOrganisationId)
      ?.organisation?.name;
  const solrSyncHref = orgSlug
    ? `/${orgSlug}/registry${APP_ROUTE_PATHS.SOLR_SYNC}`
    : undefined;

  const handleClickRegistry = () => {
    handleNavigate(PUBLIC_ROUTES.REGISTRY_LANDING);
  };

  // ABRI-staff-only "Admin" group shown in the user menu. Solr sync is an
  // org-scoped registry route (links to the selected org); Society setup
  // creates new organisations so it is a global admin route under /account.
  const adminItems: {
    key: string;
    label: ReactNode;
    Icon: SvgIconComponent;
    onClick: () => void;
    selected: boolean;
    disabled: boolean;
  }[] = [
    {
      key: 'manageUsers',
      label: userAvatarMessages['userAvatar.menu.manageUsers.label'],
      Icon: ManageAccountsIcon,
      onClick: () => handleNavigate('/account/manage-users'),
      selected: isOnManageUsersPage,
      disabled: false,
    },
    {
      key: 'solrSync',
      label: userAvatarMessages['userAvatar.menu.solrSync.label'],
      Icon: SyncIcon,
      // Solr sync is org-scoped: if an org is resolvable go straight to its
      // sync page, otherwise fall back to the registry landing (like the
      // Registry link) so the user can pick a society first.
      onClick: () =>
        handleNavigate(solrSyncHref ?? PUBLIC_ROUTES.REGISTRY_LANDING),
      selected: isOnSolrSyncPage,
      disabled: false,
    },
    {
      key: 'societySetup',
      label: userAvatarMessages['userAvatar.menu.societySetup.label'],
      Icon: SettingsOutlinedIcon,
      onClick: () => handleNavigate('/account/society-setup'),
      selected: isOnSocietySetupPage,
      disabled: false,
    },
  ];

  const handleClickLogout = () => {
    setAnchorEl(null);
    setExpanded(false);
    logout();
  };

  const getInitials = () => {
    if (userProfile?.firstName) {
      return (
        (userProfile.firstName?.at(0) || '') +
        (userProfile.lastName?.at(0) || '')
      );
    }
    if (userProfile?.preferredName) {
      return userProfile.preferredName.at(0) || '';
    }
    // Fallback when the profile fetch failed: use the session email so the
    // avatar isn't a blank circle.
    return userEmail?.at(0)?.toUpperCase() || '';
  };

  const getName = () => {
    if (userProfile?.firstName) {
      return (
        userProfile.firstName +
        (userProfile.lastName ? ` ${userProfile.lastName}` : '')
      );
    }
    return (
      userProfile?.preferredName || userProfile?.displayName || userEmail || ''
    );
  };

  const getAriaExpanded = () => {
    if (inline) return expanded;
    return open ? 'true' : undefined;
  };

  const trigger = (
    <Box
      aria-controls={!inline && open ? 'user-profile-menu' : undefined}
      aria-haspopup={!inline ? 'true' : undefined}
      aria-expanded={getAriaExpanded()}
      id="user-profile-button"
      onClick={handleClickUserProfileButton}
      sx={{
        flexGrow: 1,
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        width: inline ? '100%' : undefined,
      }}
    >
      <DocumentAvatar
        documentTypeCode={DocumentTypeCode.Avatar}
        documentEntityId={userProfile?.userProfileEntityId}
        sx={{
          bgcolor: 'primary.main',
          color: 'common.white',
          ...(compact ? { width: 28, height: 28, fontSize: '0.75rem' } : {}),
        }}
      >
        {getInitials()}
      </DocumentAvatar>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          paddingLeft: compact ? '8px' : '12px',
        }}
      >
        <Typography
          variant={compact ? 'bodysmall' : 'titlemedium'}
          sx={{ color: 'text.primary' }}
        >
          {getName()}
        </Typography>
        {userOrganisationsFound && !compact && (
          <Typography
            variant="bodysmall"
            sx={{
              color: 'text.secondary',
            }}
          >
            {UserRole[userOrganisationsFound.roleId!]}
          </Typography>
        )}
      </Box>
      <Box sx={{ flexGrow: 1 }} />
      {inline || trailingIcon === 'chevron' ? (
        <ExpandMoreIcon
          sx={{
            fontSize: '22px',
            color: 'text.primary',
            transform: (inline ? expanded : open)
              ? 'rotate(180deg)'
              : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
          }}
        />
      ) : (
        <MenuIcon
          sx={{
            fontSize: compact ? '24px' : '48px',
            color: 'text.primary',
          }}
        />
      )}
    </Box>
  );

  if (inline) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        {trigger}
        <Collapse in={expanded} unmountOnExit>
          <Box sx={{ pt: 0.5, pb: 0.25 }}>
            <Box
              sx={{
                ...inlineItemSx,
                color: isOnAccountPage ? 'primary.main' : 'text.secondary',
              }}
              onClick={handleClickAccount}
            >
              <PersonOutlinedIcon
                sx={{
                  fontSize: 18,
                  color: isOnAccountPage ? 'primary.main' : 'text.secondary',
                }}
              />
              {userAvatarMessages['userAvatar.menu.account.label']}
            </Box>
            <Box
              sx={{
                ...inlineItemSx,
                color: isOnRegistryPage ? 'primary.main' : 'text.secondary',
              }}
              onClick={handleClickRegistry}
            >
              <AppRegistrationOutlinedIcon
                sx={{
                  fontSize: 18,
                  color: isOnRegistryPage ? 'primary.main' : 'text.secondary',
                }}
              />
              {userAvatarMessages['userAvatar.menu.registry.label']}
            </Box>
            {isAbriStaff ? (
              <>
                <Box
                  sx={{
                    ...inlineItemSx,
                    color: isOnAdminPage ? 'primary.main' : 'text.secondary',
                  }}
                  onClick={() => setAdminExpanded((prev) => !prev)}
                >
                  <AdminPanelSettingsOutlinedIcon
                    sx={{
                      fontSize: 18,
                      color: isOnAdminPage ? 'primary.main' : 'text.secondary',
                    }}
                  />
                  {userAvatarMessages['userAvatar.menu.admin.label']}
                  <Box sx={{ flexGrow: 1 }} />
                  {adminExpanded ? (
                    <ExpandLessIcon sx={{ fontSize: 18 }} />
                  ) : (
                    <ExpandMoreIcon sx={{ fontSize: 18 }} />
                  )}
                </Box>
                <Collapse in={adminExpanded} unmountOnExit>
                  {adminItems.map(({ key, label, Icon, onClick, selected }) => (
                    <Box
                      key={key}
                      sx={{
                        ...inlineItemSx,
                        pl: '24px',
                        color: selected ? 'primary.main' : 'text.secondary',
                      }}
                      onClick={onClick}
                    >
                      <Icon
                        sx={{
                          fontSize: 18,
                          color: selected ? 'primary.main' : 'text.secondary',
                        }}
                      />
                      {label}
                    </Box>
                  ))}
                </Collapse>
              </>
            ) : null}
            <Divider sx={{ my: '4px' }} />
            <Box sx={inlineItemSx} onClick={handleClickLogout}>
              <LogoutOutlinedIcon
                sx={{ fontSize: 18, color: 'text.secondary' }}
              />
              {userAvatarMessages['userAvatar.menu.logout.label']}
            </Box>
          </Box>
        </Collapse>
      </Box>
    );
  }

  return (
    <>
      {trigger}
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        id="user-profile-menu"
        onClose={handleCloseMenu}
        autoFocus={false}
        open={open}
        slotProps={{
          list: {
            'aria-labelledby': 'user-profile-button',
            autoFocusItem: false,
            sx: { py: '4px' },
          },
          paper: {
            sx: { minWidth: 180, borderRadius: '8px', mt: '4px' },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      >
        <MenuItem
          sx={{
            ...menuItemSx,
            color: isOnAccountPage ? 'primary.main' : undefined,
            '& .MuiListItemIcon-root': {
              minWidth: 0,
              color: isOnAccountPage ? 'primary.main' : 'text.secondary',
            },
          }}
          selected={isOnAccountPage}
          onClick={handleClickAccount}
        >
          <ListItemIcon>
            <PersonOutlinedIcon fontSize="small" />
          </ListItemIcon>
          {userAvatarMessages['userAvatar.menu.account.label']}
        </MenuItem>
        <MenuItem
          sx={{
            ...menuItemSx,
            color: isOnRegistryPage ? 'primary.main' : undefined,
            '& .MuiListItemIcon-root': {
              minWidth: 0,
              color: isOnRegistryPage ? 'primary.main' : 'text.secondary',
            },
          }}
          selected={isOnRegistryPage}
          onClick={handleClickRegistry}
        >
          <ListItemIcon>
            <AppRegistrationOutlinedIcon fontSize="small" />
          </ListItemIcon>
          {userAvatarMessages['userAvatar.menu.registry.label']}
        </MenuItem>
        {isAbriStaff
          ? [
              <MenuItem
                key="admin"
                sx={{
                  ...menuItemSx,
                  color: isOnAdminPage ? 'primary.main' : undefined,
                  '& .MuiListItemIcon-root': {
                    minWidth: 0,
                    color: isOnAdminPage ? 'primary.main' : 'text.secondary',
                  },
                }}
                onClick={() => setAdminExpanded((prev) => !prev)}
              >
                <ListItemIcon>
                  <AdminPanelSettingsOutlinedIcon fontSize="small" />
                </ListItemIcon>
                {userAvatarMessages['userAvatar.menu.admin.label']}
                <Box sx={{ flexGrow: 1 }} />
                {adminExpanded ? (
                  <ExpandLessIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                )}
              </MenuItem>,
              <Collapse key="admin-items" in={adminExpanded} unmountOnExit>
                {adminItems.map(
                  ({ key, label, Icon, onClick, selected, disabled }) => (
                    <MenuItem
                      key={key}
                      disabled={disabled}
                      selected={selected}
                      onClick={onClick}
                      sx={{
                        ...menuItemSx,
                        pl: '32px',
                        color: selected ? 'primary.main' : undefined,
                        '& .MuiListItemIcon-root': {
                          minWidth: 0,
                          color: selected ? 'primary.main' : 'text.secondary',
                        },
                      }}
                    >
                      <ListItemIcon>
                        <Icon fontSize="small" />
                      </ListItemIcon>
                      {label}
                    </MenuItem>
                  ),
                )}
              </Collapse>,
              <Divider key="admin-divider" sx={{ my: '4px' }} />,
            ]
          : null}
        <MenuItem sx={menuItemSx} onClick={handleClickLogout}>
          <ListItemIcon>
            <LogoutOutlinedIcon fontSize="small" />
          </ListItemIcon>
          {userAvatarMessages['userAvatar.menu.logout.label']}
        </MenuItem>
      </Menu>
    </>
  );
}
